// The microphone choice, shared (Adam, 14 Sep).
//
// The picker used to be a caret beside the mic in the composer, which the
// composer's control row turned into a second white disc. Now it lives in
// Session options on both pages, away from the dictation hook that uses it.
// So the choice is one module-level store: the dialog and the hook read the
// same snapshot through useSyncExternalStore, a change in one is seen by the
// other in the same render, and the preference persists in localStorage
// under the key dictation always used.

import { useEffect, useSyncExternalStore } from 'react'

export interface AudioDevice {
  deviceId: string
  label: string
}

const STORAGE_KEY = 'preferred-mic-device-id'

interface Snapshot {
  devices: AudioDevice[]
  selectedDeviceId: string | null
}

function loadPreferred(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function savePreferred(deviceId: string | null) {
  try {
    if (deviceId) localStorage.setItem(STORAGE_KEY, deviceId)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // localStorage unavailable
  }
}

let snapshot: Snapshot = { devices: [], selectedDeviceId: loadPreferred() }
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach(l => l())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function getSnapshot() {
  return snapshot
}

/** Choose a microphone for every dictation from now on; null means the system default. */
export function selectMicrophone(deviceId: string | null) {
  savePreferred(deviceId)
  snapshot = { ...snapshot, selectedDeviceId: deviceId }
  emit()
}

/** The chosen microphone right now, for code outside React. */
export function selectedMicrophone(): string | null {
  return snapshot.selectedDeviceId
}

/**
 * Re-read the audio inputs. Labels only appear once the page has been granted
 * the microphone, so the dictation hook calls this after each getUserMedia.
 */
export async function refreshMicrophones() {
  try {
    const all = await navigator.mediaDevices.enumerateDevices()
    const inputs = all
      .filter(d => d.kind === 'audioinput' && d.deviceId)
      .map(d => ({ deviceId: d.deviceId, label: d.label || `Microphone (${d.deviceId.slice(0, 8)})` }))
    snapshot = { ...snapshot, devices: inputs }
    emit()
  } catch {
    // enumerateDevices not available
  }
}

/** Test seam: forget the choice and the device list. */
export function resetMicrophonesForTests() {
  savePreferred(null)
  snapshot = { devices: [], selectedDeviceId: null }
  emit()
}

export function useMicrophones() {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  // Enumerate on first use and again whenever a device is plugged in or
  // pulled; the listener is what needs cleaning up.
  useEffect(() => {
    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
    if (!md?.enumerateDevices) return
    refreshMicrophones()
    if (!md.addEventListener) return
    md.addEventListener('devicechange', refreshMicrophones)
    return () => md.removeEventListener('devicechange', refreshMicrophones)
  }, [])

  return {
    devices: snap.devices,
    selectedDeviceId: snap.selectedDeviceId,
    selectDevice: selectMicrophone,
    refresh: refreshMicrophones,
  }
}
